import {isBrowser} from '@github-ui/brand-core/utils/platform'
import {useAnalytics} from '@github-ui/use-analytics'
import {verifiedFetch} from '@github-ui/verified-fetch'
import {useCallback, useEffect, useRef, useState} from 'react'

// eslint-disable-next-line import/no-namespace
import * as Data from './../../data'
import ProductAnnouncementBanner from './../ProductAnnouncementBanner/ProductAnnouncementBanner'
import ProductAnnouncementModal from './../ProductAnnouncementModal/ProductAnnouncementModal'
import {COPY} from './ProductAnnouncement.data'
import type {
  ProductAnnouncementAnalytics,
  ProductAnnouncementBannerEvent,
  ProductAnnouncementBannerEventPayload,
  ProductAnnouncementData,
  ProductAnnouncementModalEvent,
  ProductAnnouncementModalEventPayload,
} from './ProductAnnouncement.types'

export interface ProductAnnouncementProps {
  id?: string
  announcementId: string
  data?: ProductAnnouncementData
  label?: string
  shouldShowTabCtas?: boolean
  shouldShowMainCta?: boolean
  useLocalStorage?: boolean
  // When provided, ProductAnnouncement emits its own visible/click/dismiss analytics
  // (via @github-ui/use-analytics) using each element's `analyticsAction` from the data
  // model. Consumers that instrument manually can omit this and use onBannerEvent/onModalEvent.
  analytics?: ProductAnnouncementAnalytics
  // When provided, ProductAnnouncement POSTs to this path on modal dismissal (server-side
  // notice dismissal). No request is made when absent or when navigating via a CTA href.
  dismissPath?: string
  onClose?: () => void
  onBannerEvent?: (event: ProductAnnouncementBannerEvent, data?: ProductAnnouncementBannerEventPayload) => void
  onModalEvent?: (event: ProductAnnouncementModalEvent, data?: ProductAnnouncementModalEventPayload) => void
}

const defaultProps: Partial<ProductAnnouncementProps> = {}

const resolveAnnouncementData = (
  announcementId: ProductAnnouncementProps['announcementId'],
  injectedData?: ProductAnnouncementData,
) => {
  if (injectedData) return injectedData

  const rawData = Data[announcementId as keyof typeof Data]
  return typeof rawData === 'function' ? rawData() : rawData
}

const ProductAnnouncement = (props: ProductAnnouncementProps) => {
  const initializedProps: ProductAnnouncementProps = {...defaultProps, ...props}
  const {
    id,
    announcementId,
    data: injectedData,
    label,
    shouldShowTabCtas,
    shouldShowMainCta,
    useLocalStorage,
    analytics,
    dismissPath,
    onClose: onCloseProp,
    onBannerEvent,
    onModalEvent,
  } = initializedProps

  const {sendAnalyticsEvent} = useAnalytics({optional: true})

  const emitClickAnalytics = useCallback(
    (payload?: ProductAnnouncementModalEventPayload | ProductAnnouncementBannerEventPayload) => {
      if (!analytics || !payload?.analyticsAction) return
      sendAnalyticsEvent('analytics.click', undefined, {
        category: analytics.category,
        action: payload.analyticsAction,
        label: analytics.label ?? '',
      })
    },
    [analytics, sendAnalyticsEvent],
  )

  const emitVisibleAnalytics = useCallback(() => {
    if (!analytics) return
    sendAnalyticsEvent('analytics.visible', undefined, {
      category: analytics.category,
      action: 'visible',
      label: analytics.label ?? '',
    })
  }, [analytics, sendAnalyticsEvent])

  const handleBannerEvent = useCallback(
    (event: ProductAnnouncementBannerEvent, payload?: ProductAnnouncementBannerEventPayload) => {
      if (event === 'displayed') emitVisibleAnalytics()
      emitClickAnalytics(payload)
      onBannerEvent?.(event, payload)
    },
    [emitVisibleAnalytics, emitClickAnalytics, onBannerEvent],
  )

  const handleModalEvent = useCallback(
    (event: ProductAnnouncementModalEvent, payload?: ProductAnnouncementModalEventPayload) => {
      if (event === 'opened') emitVisibleAnalytics()
      emitClickAnalytics(payload)
      onModalEvent?.(event, payload)
    },
    [emitVisibleAnalytics, emitClickAnalytics, onModalEvent],
  )

  const [isInitialized, setIsInitialized] = useState<boolean>(false)
  const [isBannerVisible, setIsBannerVisible] = useState<boolean>(false)
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false)
  const announcementIdRef = useRef<ProductAnnouncementProps['announcementId']>(announcementId)

  const getLocalStorageKey = useCallback(
    () => `productAnnouncement.${announcementId}.${id || window.location.pathname}:dismissed`,
    [announcementId, id],
  )

  useEffect(() => {
    if (announcementIdRef.current !== announcementId) setIsModalOpen(false)
    announcementIdRef.current = announcementId
  }, [announcementId])

  useEffect(() => {
    if (isBrowser() && useLocalStorage && !isBannerVisible && isInitialized) {
      localStorage.setItem(getLocalStorageKey(), 'true')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBannerVisible, useLocalStorage])

  useEffect(() => {
    if (isBrowser()) {
      const isDismissed = useLocalStorage && localStorage.getItem(getLocalStorageKey()) === 'true'

      const announcementData = resolveAnnouncementData(announcementId, injectedData)
      const announcementHasBanner = announcementData && 'banner' in announcementData

      if (!isDismissed) {
        if (announcementHasBanner) {
          // eslint-disable-next-line react-hooks/set-state-in-effect
          setIsBannerVisible(true)
        } else {
          setIsModalOpen(true)
        }
      }

      setIsInitialized(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const data = resolveAnnouncementData(announcementId, injectedData)
  if (!data) return null

  const hasBanner = 'banner' in data

  return (
    <>
      {hasBanner && (
        <ProductAnnouncementBanner
          {...data.banner}
          isVisible={isBannerVisible}
          onClick={() => setIsModalOpen(true)}
          onDismiss={() => setIsBannerVisible(false)}
          aria={COPY.aria.banner}
          announcementId={announcementId}
          onEvent={handleBannerEvent}
        />
      )}

      <ProductAnnouncementModal
        {...data.modal}
        label={label || data.modal.label}
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false)
          if (!hasBanner && useLocalStorage && isBrowser()) {
            localStorage.setItem(getLocalStorageKey(), 'true')
          }
          if (analytics) {
            sendAnalyticsEvent('analytics.click', undefined, {
              category: analytics.category,
              action: 'dismiss',
              label: analytics.label ?? '',
            })
          }
          if (dismissPath && isBrowser()) {
            void verifiedFetch(dismissPath, {method: 'POST'})
          }
          onCloseProp?.()
        }}
        aria={COPY.aria.modal}
        shouldShowTabCtas={shouldShowTabCtas}
        shouldShowMainCta={shouldShowMainCta}
        onEvent={handleModalEvent}
      />
    </>
  )
}

export default ProductAnnouncement
